// The service that call the API to get the data from API_URL
import qs from 'qs';
import type User from '../../auth/domain/auth';
import useParkstoryApi, { Target, type ApiResponse } from '../../common/data/parkstory/parkstory';
import useGroupConvertor, { type Group, type ApiGroup } from '../domain/group';
import useGroupConfig, {
    type GroupConfig,
    type GroupConfigEnterprise,
} from '../domain/groupConfig';
import { type PostInvitation } from '../domain/member';
import usePacket, { type ApiPacket, type Packet } from '../domain/packet';

import { type PostGuest } from '@/group/domain/guest.ts';
import useInvitation, { type ApiInvitation, type Invitation } from '@/group/domain/invitation';
import { type ApiEntity } from '@/common/domain/api';
import { logException, logTrace } from '@/common/utils/logger';
import useFleetConvertor, { type ApiFleetAttributes, type Fleet } from '@/fleet/domain/fleet';


type GroupConfigReturnType = ApiResponse<GroupConfig | GroupConfigEnterprise>;

const isAxiosErrorWithResponse = (error: unknown): error is { response?: { data?: any; status?: number } } => {
    return typeof error === 'object' && error !== null && 'response' in error;
};

interface QueryParams {
    [key: string]: any;
}

const buildQueryString = (params: QueryParams): string => {
    return qs.stringify(params, { arrayFormat: 'brackets', skipNulls: true });
};

/**
 * The populate shape for a user-group: every relation `apiGroupToGroup` actually maps, with the
 * packet deep-populated down to `packet.config.features` (which the access-profile gate reads).
 *
 * Shared by every group query so the shape cannot drift per call site. Keep it to REAL backend
 * relations only — Strapi rejects an unknown populate key with a 400. In particular do NOT add
 * `assignedFleets` (derived client-side from `relatesTo`, see `resolveAssignedFleets`) or `cover`
 * (a scalar string, returned without populating).
 */
const populateForGroup = {
    relatesTo: true,
    groupConfigs: {
        populate: {
            groupConfig: {
                on: {
                    'user.enterprise': {
                        populate: {
                            address: true,
                            contact: true,
                            superAdminsGroup: {
                                populate: {
                                    members: true,
                                },
                            },
                            superFleet: true,
                        },
                    },
                },
            },
        },
    },
    members: true,
    guests: true,
    invitations: { populate: true },
    packet: { populate: { config: { populate: '*' } } },
};

const useGroupApi = () => {
    const { get, post, put, remove, convertApiResponse } = useParkstoryApi();
    const { apiGroupsToGroups, apiGroupToGroup, groupToApiGroup } = useGroupConvertor;
    const { groupConfigToApiGroupConfig } = useGroupConfig;
    const { apiInvitationToInvitation } = useInvitation;
    const { apiPacketsToPackets } = usePacket;
    const { apiFleetToFleet } = useFleetConvertor;

    // Fleets are get in an array of objects like { group, fleet }
    const apiGetGroupFleet = async (groupDocumentId: string, queryParams?: QueryParams): Promise<Group & { fleet: Fleet } | null> => {
        try {
            const response = await get<ApiEntity<ApiFleetAttributes>>(
                Target.Vehicle,
                `user-groups/${groupDocumentId}${queryParams ? `?${buildQueryString(queryParams)}` : ''}`,
            );
            if (response.status.code === 200 && response.data) {
                const group = convertApiResponse(response, apiGroupToGroup)?.data ?? null;
                const fleet = convertApiResponse(response, apiFleetToFleet)?.data ?? null;

                if (group && fleet) {
                    return { ...group, fleet };
                }
            } else if (response.status.code === 200 && !response.data) {
                return null;
            } else {
                throw new Error('Error fetching group fleet');
            }
            return null;
        } catch (error) {
            logException(error, { groupId: groupDocumentId });
            throw error;
        }
    };

    const apiGetGroups = async (): Promise<ApiResponse<Group[]>> => {
        try {
            const queryParams = buildQueryString({
                // Explicit populate (not `*`) so the packet is deep-populated down to
                // `packet.config.features`: `*` populates one level only, so the packet came back
                // without its config — which flipped `useIsAccessProfileEnabled` to false and made
                // the "My access" sidebar item disappear when this list was (re)loaded on the
                // My-access screens.
                //
                // Reuses `populateForGroup` — the shape every other group query sends — rather than a
                // second literal. A hand-written list here drifted into asking for two keys that are
                // NOT backend relations, and Strapi answers an invalid populate key with a 400 (which
                // `*` never surfaced, since it just populates whatever exists):
                //   - `assignedFleets` is derived CLIENT-side from `relatesTo` (see
                //     `resolveAssignedFleets`); there is no such field on the user-group.
                //   - `cover` is a scalar string, and scalars are returned without being populated.
                populate: populateForGroup,
                sort: 'createdAt:asc',
            });

            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error);
            throw error;
        }
    };

    const apiGetGroupByIds = async (
        ids: number[],
    ): Promise<ApiResponse<Group[]>> => {
        try {
            const queryParams = buildQueryString({
                filters: { documentId: { $in: ids } },
                sort: 'createdAt:asc',
                populate: '*',
            });

            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error);
            throw error;
        }
    };

    // Get groups by group config type
    const apiGetGroupsByGroupConfigType = async (
        type: string,
    ): Promise<ApiResponse<Group[]>> => {
        try {
            const queryParams = buildQueryString({
                populate: '*',
                sort: 'createdAt:asc',
            });

            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups/filter-group-config/${type}?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error, { type });
            throw error;
        }
    };

    const apiGetGroupsByPrefix = async (
        type: string,
    ) => {
        try {
            const queryParams = buildQueryString({
                populate: '*',
                filters: { prefix: { $eq: type } },
                sort: 'createdAt:asc',
            });
            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error, { type });
            throw error;
        }
    };


    const apiGetGroup = async (documentId: string) => {
        try {
            const deepPopulate = {
                populate: {
                    ...populateForGroup,
                },
                // Ask the backend to include the group's assigned fleet(s) in the response.
                withFleets: true,
            };

            let response;
            response = await apiGetGroupFleet(documentId, deepPopulate);

            if (response?.data === null) {
                throw new Error('Group not found');
            }

            return response;
        } catch (error) {
            logException(error, { groupId: documentId });
            throw error;
        }
    };

    const apiGetParkingAdminsGroups = async (): Promise<ApiResponse<Group[]>> => {
        try {
            const queryParams = buildQueryString({
                filters: { prefix: { $eq: '_parkingAdmin' } },
                populate: '*',
            });
            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error);
            throw error;
        }
    };


    // Retreive group admin with groupId using relatesTo filter
    const apiGetGroupAdminsRT = async (groupId: string): Promise<Group | null> => {
        try {
            const queryParams = buildQueryString({
                populate: '*',
                filters: {
                    relatesTo: {
                        targetId: { $eq: groupId },
                        relation: { $eq: 'ownership' },
                    },
                },
                sort: 'createdAt:asc',
            });
            const response = await get<ApiGroup>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            if (Array.isArray(response.data) && response.data.length > 0) {
                const convertedResp = convertApiResponse(response, apiGroupToGroup)?.data ?? null;
                if (convertedResp !== undefined) {
                    return convertedResp;
                }
            } else if (Array.isArray(response.data) && response.data.length === 0 && response.status.code === 200) {
                return null;
            } else {
                throw Error;
            }
        } catch (error) {
            logException(error, { groupId });
            throw error;
        }
        return null;
    };

    /*
     * Retrieve ALL user-groups that own the fleet `fleetId` via an `ownership` relation
     * (sorted oldest-first). Unlike apiGetGroupAdminsRT (which collapses to the first match), this
     * returns the full list so callers can disambiguate when more than one ownership group exists for
     * the same target — e.g. a fleet-admin group that got duplicated by a check-then-create race.
     * Reused only by the fleet path. (`ownership` relations target fleets: targetModel 'fleet',
     * targetId = the fleet documentId — hence the parameter is a fleet id, not a group id.)
     */
    const apiGetOwnershipGroupsRT = async (fleetId: string): Promise<Group[]> => {
        try {
            const queryParams = buildQueryString({
                populate: '*',
                // Explicit cap: the "full list" intent must not be silently truncated by Strapi's
                // default page size (25). Race-created duplicates are 2–3 rows, so 100 is ample.
                pagination: { pageSize: 100 },
                filters: {
                    relatesTo: {
                        targetId: { $eq: fleetId },
                        relation: { $eq: 'ownership' },
                    },
                },
                sort: 'createdAt:asc',
            });
            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            if (Array.isArray(response.data) && response.status.code === 200) {
                const converted = convertApiResponse(response, apiGroupsToGroups)?.data ?? [];
                logTrace('[fleet-admin] apiGetOwnershipGroupsRT', undefined, {
                    fleetId,
                    rawCount: response.data.length,
                    convertedCount: converted.length,
                });
                return converted;
            }
            throw new Error(`Unexpected response fetching ownership groups for fleet ${fleetId}`);
        } catch (error) {
            logException(error, { fleetId });
            throw error;
        }
    };

    // Retreive group admin with groupId using relatesTo filter
    const apiGetAllGroupAdminsRT = async (): Promise<Group[] | null> => {
        try {
            const queryParams = buildQueryString({
                populate: '*',
                filters: {
                    relatesTo: {
                        targetModel: { $eq: 'userGroup' },
                        relation: { $eq: 'ownership' },
                    },
                },
                sort: 'createdAt:asc',
            });

            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            if (Array.isArray(response.data) && response.data.length > 0) {
                const convertedResp = convertApiResponse(response, apiGroupsToGroups)?.data ?? null;
                if (convertedResp !== undefined) {
                    return convertedResp;
                }
            } else if (Array.isArray(response.data) && response.data.length === 0 && response.status.code === 200) {
                return null;
            } else {
                throw Error;
            }
        } catch (error) {
            logException(error);
            throw error;
        }
        return null;
    };

    // Retreive group admin  with groupId using grpconfig filter
    const apiGetGroupAdminsGC = async (groupId: string): Promise<number> => {
        try {
            const queryParams = buildQueryString({
                filters: {
                    relation: { $eq: 'ownership' },
                    targetId: { $eq: groupId },
                },
                populate: '*',
                sort: 'createdAt:asc',
            });

            const response = await get<ApiGroup>(
                Target.Vehicle,
                `user-groups/filter-group-config/of?${queryParams}`,
            );
            if (response.status.code === 200 && (response.data === null || response.data === undefined)) {
                return -1;
            } else {
                return (convertApiResponse(response, apiGroupToGroup)?.data?.id ?? -1);
            }
        } catch (error) {
            logException(error, { groupId });
            throw error;
        }
    };


    const apiCreateGroup = async (
        group: Group,
    ): Promise<ApiResponse<Group | null>> => {
        try {
            const response = await post<ApiGroup>(
                Target.Vehicle,
                'user-groups?populate[relatesTo]=true&populate[members]=true',
                group,
            );
            return convertApiResponse(response, apiGroupToGroup);
        } catch (error) {
            logException(error, { groupId: group.documentId });
            throw error;
        }
    };

    const apiUpdateGroup = async (
        group: Group,
    ): Promise<ApiResponse<Group>> => {
        try {
            const queryParams = buildQueryString({
                populate: '*',
            });
            const response = await put<Group>(
                Target.Vehicle,
                `user-groups/${group.documentId}?${queryParams}`,
                groupToApiGroup(group),
            );
            return response;
        } catch (error) {
            logException(error, { groupId: group.documentId });
            throw error;
        }
    };

    const apiDeleteGroup = async (documentId: string): Promise<ApiResponse<Group>> => {
        try {
            const response = await remove<Group>(
                Target.Vehicle,
                `user-groups/${documentId}`,
            );
            return response;
        } catch (error) {
            logException(error, { groupId: documentId });
            throw error;
        }
    };

    const apiGetInvitation = async (
        documentId: string,
    ): Promise<ApiResponse<Invitation>> => {
        try {
            const response = await get<ApiInvitation>(Target.Vehicle, `invitations/${documentId}?populate[userGroups][populate]=*`);

            return convertApiResponse(response, apiInvitationToInvitation);
        } catch (error) {
            logException(error, { invitationId: documentId });
            throw error;
        }
    };

    const apiConfirmInvitationToGroup = async (
        code: number,
        invitation: string,
        groupDocumentId: string,
    ): Promise<ApiResponse<Invitation>> => {
        try {
            const body = {
                code,
                invitation
            };

            const response = await put<Invitation>(
                Target.Vehicle,
                `user-groups/${groupDocumentId}/confirm/user`,
                body,
            );
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentId });
            throw error;
        }
    };

    // Delete Invitation
    const apiDeleteInvitation = async (groupDocumentId: string, invitationId: string): Promise<ApiResponse<Invitation>> => {
        try {
            const response = await post<Invitation>(Target.Vehicle, `user-groups/${groupDocumentId}/revoke-invitations`, { invitations: [invitationId] });
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentId, invitationId });
            throw error;
        }
    };

    // Add user to group
    const apiAddUserToGroup = async (
        groupDocumentId: string,
        userId: number,
    ): Promise<ApiResponse<Group>> => {
        try {
            const response = await post<Group>(
                Target.Vehicle,
                `user-groups/${groupDocumentId}/users`,
                { data: userId },
            );
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentId, userId });
            throw error;
        }
    };

    // This does not seems legit:
    const apiUpdateMember = async (user: User): Promise<ApiResponse<Group>> => {
        try {
            const response = await put<Group>(
                Target.Vehicle,
                `user-profile/${user.ref}`,
                user,
            );
            return response;
        } catch (error) {
            logException(error, { userRef: user.ref });
            throw error;
        }
    };

    const apiInviteMember = async (groupDocumentId: string, member: PostInvitation): Promise<ApiResponse<Group>> => {
        try {
            const response = await post<Group>(
                Target.Vehicle,
                `user-groups/${groupDocumentId}/invite/user`,
                member,
            );
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentId });
            throw error;
        }
    };


    // Remove user from group
    const apiRemoveMemberFromGroup = async (groupDocumentId: string, userRef: string): Promise<ApiResponse<Group>> => {
        try {
            const response = await put<Group>(Target.Vehicle, `user-groups/${groupDocumentId}/revoke/user`, {
                userRef
            });
            return response;
        } catch (error: any) {
            logException(error, { groupId: groupDocumentId, userRef });
            let errorMessage = error.message;
            if (isAxiosErrorWithResponse(error) && error.response?.data) {
                if (error.response.data.error?.message) {
                    errorMessage = error.response.data.error.message;
                } else if (error.response.data.message || error.response.data.status) {
                    errorMessage = error.response.data.message;
                }
            }
            const wrappedError = new Error(errorMessage);
            (wrappedError as { status?: number }).status = isAxiosErrorWithResponse(error) ? error.response?.status : undefined;
            throw wrappedError;
        }
    };

    const apiInviteGuest = async (groupDocumentId: string, guest: PostGuest): Promise<ApiResponse<Group>> => {
        try {
            const response = await post<Group>(
                Target.Vehicle,
                `user-groups/${groupDocumentId}/invite/guest`,
                guest,
            );
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentId });
            throw error;
        }
    };

    const apiRemoveGuestFromGroup = async (groupDocumentId: string, guestId: number): Promise<ApiResponse<Group>> => {
        try {
            const response = await remove<Group>(Target.Vehicle, `user-groups/${groupDocumentId}/remove-guest/${guestId}`);
            return response;
        } catch (error: any) {
            logException(error, { groupId: groupDocumentId, guestId });
            let errorMessage = error.message;
            if (isAxiosErrorWithResponse(error) && error.response?.data) {
                if (error.response.data.error?.message) {
                    errorMessage = error.response.data.error.message;
                } else if (error.response.data.message || error.response.data.status) {
                    errorMessage = error.response.data.message;
                }
            }
            const wrappedError = new Error(errorMessage);
            (wrappedError as { status?: number }).status = isAxiosErrorWithResponse(error) ? error.response?.status : undefined;
            throw wrappedError;
        }
    };
    const apiGetCompanies = async (withSuperAdminsGroup?: boolean): Promise<any> => {
        const populate = withSuperAdminsGroup
            ? populateForGroup
            : undefined;

        const queryParams = buildQueryString({
            populate,
            'pagination[page]': 1,
            'pagination[pageSize]': 200,
            sort: 'createdAt:asc',
        });

        try {
            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups/filter-group-config/enterprise?${queryParams}`,
            );

            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error);
            throw error;
        }
    };


    // Grant access to a group
    const apiGrantAccessToGroup = async (
        groupDocumentId: string,
        adminGroupDocumentId: string,
    ): Promise<ApiResponse<Group>> => {
        try {
            const response = await post<Group>(
                Target.Vehicle,
                `user-groups/${groupDocumentId}/grant`,
                { group: adminGroupDocumentId, verbs: ['READ', 'CREATE', 'UPDATE', 'DELETE', 'GRANT', 'REVOKE', 'RA'] },
            );
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentId, adminGroupId: adminGroupDocumentId });
            throw error;
        }
    };

    // Grant access to a group config
    const apiGrantAccessToGroupConfig = async (
        groupConfigDocumentId: string,
        adminGroupDocumentId: string,
    ): Promise<ApiResponse<Group>> => {
        try {
            const response = await post<Group>(
                Target.Vehicle,
                `group-configs/${groupConfigDocumentId}/grant`,
                { group: adminGroupDocumentId, verbs: ['READ', 'CREATE', 'UPDATE', 'DELETE', 'GRANT', 'REVOKE'] },
            );
            return response;
        } catch (error) {
            logException(error, { groupConfigId: groupConfigDocumentId, adminGroupId: adminGroupDocumentId });
            throw error;
        }
    };


    // Grant access to a group
    const apiGrantAccessToPacket = async (
        packetDocumentId: string,
        adminGroupDocumentId: string,
    ): Promise<ApiResponse<Packet>> => {
        try {
            const response = await post<Packet>(
                Target.Vehicle,
                `packets/${packetDocumentId}/grant`,
                { group: adminGroupDocumentId, verbs: ['READ', 'CREATE', 'UPDATE', 'DELETE', 'GRANT', 'REVOKE'] },
            );
            return response;
        } catch (error) {
            logException(error, { packetId: packetDocumentId, adminGroupId: adminGroupDocumentId });
            throw error;
        }
    };

    // Reset 2FA for one member
    const apiReset2FA = async (
        groupDocumentId: string,
        userRef: string,
    ): Promise<ApiResponse<unknown>> => {
        try {
            const response = await get<unknown>(
                Target.Vehicle,
                `user-groups/${groupDocumentId}/reset/user/${userRef}`,
            );
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentId, userRef });
            throw error;
        }
    };

    const apiGetQRCodePdf = async (groupId: string, guestId: string) => {
        try {
            const response = await get<any>(
                Target.Vehicle,
                `user-groups/${groupId}/guest-qrcode/${guestId}`,
                'blob'
            );
            return response;
        } catch (error) {
            logException(error, { groupId, guestId });
            throw error;
        }
    };


    // Grant access to a group
    const apiGrantReadToGroup = async (
        groupDocumentIdApplied: string,
        groupDocumentIdGranted: string,
    ): Promise<ApiResponse<Group>> => {
        try {
            const response = await post<Group>(
                Target.Vehicle,
                `user-groups/${groupDocumentIdApplied}/grant`,
                { group: groupDocumentIdGranted, verbs: ['READ'] },
            );
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentIdApplied, grantedGroupId: groupDocumentIdGranted });
            throw error;
        }
    };

    // Revoke access to a group
    const apiRevokeAccessToGroup = async (
        groupDocumentId: string,
        adminGroupDocumentId: string,
    ): Promise<ApiResponse<Group>> => {
        try {
            const response = await put<Group>(
                Target.Vehicle,
                `user-groups/${groupDocumentId}/revoke/user`,
                { data: adminGroupDocumentId },
            );
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentId, adminGroupId: adminGroupDocumentId });
            throw error;
        }
    };


    // Create group config
    const apiCreateGroupConfig = async (
        groupConfig: GroupConfig,
        userGroupsDocumentId: string
    ): Promise<GroupConfigReturnType> => {
        try {
            const response = await post<GroupConfig | GroupConfigEnterprise>(
                Target.Vehicle,
                'group-configs',
                { ...groupConfigToApiGroupConfig(groupConfig), userGroups: userGroupsDocumentId },
            );

            const simplifiedResponse: GroupConfigReturnType = {
                data: response.data,
                status: response.status,
            };

            return simplifiedResponse;
        } catch (error) {
            logException(error, { userGroupId: userGroupsDocumentId });
            throw error;
        }
    };

    // Update group config
    const apiUpdateGroupConfig = async (
        groupConfig: GroupConfig,
    ): Promise<GroupConfigReturnType> => {
        try {
            const response = await put<GroupConfig>(
                Target.Vehicle,
                `group-configs/${groupConfig.documentId}?populate=*`,
                groupConfigToApiGroupConfig(groupConfig),
            );
            const simplifiedResponse: GroupConfigReturnType = {
                data: response.data,
                status: response.status,
            };

            return simplifiedResponse;
        } catch (error) {
            logException(error, { groupConfigId: groupConfig.documentId });
            throw error;
        }
    };

    // Delete group config
    const apiDeleteGroupConfig = async (
        documentId: string,
    ): Promise<ApiResponse<GroupConfig>> => {
        try {
            const response = await remove<GroupConfig>(
                Target.Vehicle,
                `group-configs/${documentId}`,
            );

            return response;
        } catch (error) {
            logException(error, { groupConfigId: documentId });
            throw error;
        }
    };

    // Get packets and filter by group
    const apiGetPackets = async (): Promise<ApiResponse<Packet[]>> => {
        try {
            const sort = 'sort=createdAt:asc';
            const response = await get<ApiPacket[]>(
                Target.Vehicle,
                'packets?populate[userGroup][populate]=*&populate[fleet][populate]=*&populate[config][populate]=*' + '&' + sort,
            );
            return convertApiResponse(response, apiPacketsToPackets);
        } catch (error) {
            logException(error);
            throw error;
        }
    };

    // Remove packets
    const apiDeletePackets = async (documentId: string): Promise<ApiResponse<any>> => {
        try {
            const response = await remove<ApiPacket[]>(
                Target.Vehicle,
                'packets/' + documentId,
            );
            return response;
        } catch (error) {
            logException(error, { packetId: documentId });
            throw error;
        }
    };

    /*
     * Packets carry no fleet-admin control param. An earlier contract passed `fleetAdminRefs` here for
     * the backend to reconcile the fleet's admin group, but that reconcile resolved the fleet from
     * `packet.fleet` — a relation nothing ever writes — so it never ran and has been removed backend-side.
     * The fleet-admin roster is now set explicitly via groupController's `replaceFleetAdmins`, which
     * treats the fleet-admin group as an ordinary user-group (apiUpdateGroup/apiCreateGroup).
     */
    const apiCreatePacket = async (
        packet: Packet,
    ): Promise<ApiResponse<Packet>> => {
        try {
            const response = await post<Packet>(
                Target.Vehicle,
                'packets',
                {
                    resaClassRef: packet.resaClassRef,
                    reservedNumber: packet.reservedNumber ?? 0,
                    overflowNumber: packet.overflowNumber,
                    isOverflowFixed: packet.isOverflowFixed,
                    config: packet.config,
                },
            );
            return response;
        } catch (error) {
            logException(error, { packetId: packet.documentId });
            throw error;
        }
    };

    // Update packet. See apiCreatePacket on why there is no fleet-admin control param.
    const apiUpdatePacket = async (
        packet: Packet,
    ): Promise<ApiResponse<Packet>> => {
        try {
            const response = await put<Packet>(
                Target.Vehicle,
                `packets/${packet.documentId}`,
                {
                    resaClassRef: packet.resaClassRef,
                    reservedNumber: packet.reservedNumber ?? 0,
                    overflowNumber: packet.overflowNumber,
                    isOverflowFixed: packet.isOverflowFixed,
                    config: packet.config,
                },
            );
            return response;
        } catch (error) {
            logException(error, { packetId: packet.documentId });
            throw error;
        }
    };

    // Link a packet to a group (relation is owned by user-group side)
    const apiSetGroupPacket = async (
        groupDocumentId: string,
        packetDocumentId: string,
    ): Promise<ApiResponse<Group>> => {
        try {
            const response = await put<Group>(
                Target.Vehicle,
                `user-groups/${groupDocumentId}`,
                { packet: packetDocumentId },
            );
            return response;
        } catch (error) {
            logException(error, { groupId: groupDocumentId, packetId: packetDocumentId });
            throw error;
        }
    };

    // Get by ownership groups with target Id
    const apiGetByOwnershipGroups = async (
        id: string,
    ): Promise<ApiResponse<Group[]>> => {
        try {
            const queryParams = buildQueryString({
                populate: {
                    groupConfigs: true,
                    members: true,
                    guests: true,
                },
                filters: {
                    relation: { $eq: 'ownership' },
                    targetId: { $eq: id },
                },
                'pagination[page]': 1,
                'pagination[pageSize]': 200,
                sort: 'createdAt:asc',
            });
            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups/filter-group-config/of?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error, { targetId: id });
            throw error;
        }
    };

    const apiGetGroupsByRelations = async (
        target: string,
        relation: string
    ): Promise<ApiResponse<Group[]>> => {
        try {
            const queryParams = buildQueryString({
                populate: {
                    ...populateForGroup
                },
                'pagination[page]': 1,
                'pagination[pageSize]': 200,
                filters: {
                    relatesTo: {
                        relation: { $eq: relation },
                        targetModel: { $eq: target },
                    },
                },
            });
            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error, { target, relation });
            throw error;
        }
    };
    const apiGetGroupsByOneRelation = async (
        id: string,
        target: string,
        relation: string
    ): Promise<ApiResponse<Group[]>> => {
        try {
            const queryParams = buildQueryString({
                populate: {
                    ...populateForGroup
                },
                filters: {
                    relatesTo: {
                        relation: { $eq: relation },
                        targetModel: { $eq: target },
                        targetId: { $eq: id },
                    },
                },
                sort: 'createdAt:asc',
            });
            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error, { targetId: id, target, relation });
            throw error;
        }
    };


    // Get inherited grps using relatesTo
    const apiGetSubGroupsByInheritanceRT = async (
        id: string,
    ): Promise<ApiResponse<Group[]>> => {
        try {
            const queryParams = buildQueryString({
                populate: {
                    ...populateForGroup
                },
                filters: {
                    relatesTo: {
                        relation: { $eq: 'inheritance' },
                        targetId: { $eq: id },
                    },
                },
                sort: 'createdAt:asc',
            });
            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error, { groupId: id });
            throw error;
        }
    };

    // Get inherited groups with target Id using groupConfig
    const apiGetSubGroupsByInheritanceGC = async (
        id: string, // group our group inherits from
    ): Promise<ApiResponse<Group[]>> => {
        try {
            const queryParams = buildQueryString({
                populate: {
                    ...populateForGroup
                },
                filters: {
                    targetId: { $eq: id },
                },
                sort: 'createdAt:asc',
            });
            const response = await get<ApiGroup[]>(
                Target.Vehicle,
                `user-groups/filter-group-config/of?${queryParams}`,
            );
            return convertApiResponse(response, apiGroupsToGroups);
        } catch (error) {
            logException(error, { groupId: id });
            throw error;
        }
    };


    return {
        apiAddUserToGroup,
        apiConfirmInvitationToGroup,
        apiCreateGroup,
        apiCreateGroupConfig,
        apiCreatePacket,
        apiDeleteGroup,
        apiDeleteGroupConfig,
        apiDeleteInvitation,
        apiDeletePackets,
        apiGetByOwnershipGroups,
        apiGetCompanies,
        apiGetGroup,
        apiGetGroupAdminsGC,
        apiGetGroupAdminsRT,
        apiGetOwnershipGroupsRT,
        apiGetQRCodePdf,
        apiGetGroupByIds,
        apiGetGroups,
        apiGetGroupsByGroupConfigType,
        apiGetGroupsByOneRelation,
        apiGetGroupsByPrefix,
        apiGetGroupsByRelations,
        apiGetInvitation,
        apiGetPackets,
        apiGetSubGroupsByInheritanceGC,
        apiGetSubGroupsByInheritanceRT,
        apiGrantAccessToGroup,
        apiGrantAccessToGroupConfig,
        apiGrantAccessToPacket,
        apiGrantReadToGroup,
        apiInviteGuest,
        apiInviteMember,
        apiRemoveGuestFromGroup,
        apiRemoveMemberFromGroup,
        apiRevokeAccessToGroup,
        apiUpdateGroup,
        apiUpdateGroupConfig,
        apiReset2FA,
        apiGetAllGroupAdminsRT,
        apiUpdateMember,
        apiUpdatePacket,
        apiSetGroupPacket,
        apiGetParkingAdminsGroups
    };
};

export default useGroupApi;